import { queryClient } from "@istari-frontend/api-client";
import { useMutation, useQuery } from "@tanstack/react-query";

import { QUERY_GC_TIME_MS_30M, ZITADEL_RELOAD_DELAY } from "../constants";
import { ZitadelRoles } from "../enums";
import { adminServiceV1, adminServiceV2 } from "../services";
import { paths } from "../services/paths";

export interface UserObject {
  email?: string | undefined;
  firstName: string;
  lastName: string;
  isAdmin?: boolean;
  userId: string;
}

interface NewUserFormData {
  userName: string;
  profile: {
    firstName: string;
    lastName: string;
  };
  email: {
    email: string;
  };
  isAdmin: boolean;
}

interface NewUserData {
  formData: NewUserFormData;
  /** The caller's organization, whose granted project receives the new admin's grant. */
  organizationId: string | undefined;
}

interface ProjectInfo {
  id?: string;
  roleKeys: string[];
  projectId: string;
  projectGrantId: string;
  userId?: string;
}

interface MyProjectResponse {
  result: ProjectInfo[];
}

interface NewUserImportResponse {
  userId: string;
  details: {
    sequence: string;
    updatedAt: string;
    resourceOwner: string;
  };
}

interface UserFormData {
  firstName: string;
  lastName: string;
  displayName: string;
}

interface DeactivateUserPayload {
  userId: string;
}

interface UserMetaDetail {
  changeDate: string;
  updatedAt: string;
  resourceOwner: string;
  sequence: string;
}

interface ActiveUser {
  details: UserMetaDetail;
  human: {
    email: {
      email: string;
    };
    phone: undefined;
    profile: {
      displayName: string;
      givenName: string;
      familyName: string;
      preferredLanguage: string;
    };
    id: string;
    loginNames: string[];
    preferredLoginName: string;
    state: string;
    userName: string;
  };
  id: string;
  state: string;
  preferredLoginName: string;
  userId?: string;
}

export interface ActiveUserData {
  email: string;
  firstName: string;
  fullName: string;
  id: string;
  isAdmin: boolean;
  lastName: string;
  orgId: string;
  status: string;
  updatedAt: string;
  userId: string;
}

interface MemberList {
  displayName: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  userId: string;
}

interface OrgMembershipList {
  id: string;
  isAdmin: boolean;
  userId: string;
}

export const adminQueryKeys = {
  all: ["admin"],
  orgs: ["admin", "orgs"],
  item: (id: string | number) => [...adminQueryKeys.all, id],
};

async function getUserGrants(): Promise<ProjectInfo[]> {
  const myProjectData: MyProjectResponse = await adminServiceV1[paths.searchGrantsMethod](paths.searchGrants);
  return myProjectData?.result ?? [];
}

async function searchGrantedProjects() {
  const myGrantedProjectsData = await adminServiceV1[paths.searchGrantedProjectsMethod](paths.searchGrantedProjects);
  return myGrantedProjectsData?.result ?? [];
}

export async function getOrgMembers(): Promise<OrgMembershipList[]> {
  const userGrants = await getUserGrants();
  const response = await adminServiceV1[paths.searchOrgMembersMethod](paths.searchOrgMembers);
  const members: MemberList[] = response?.result ?? [];
  return members.map((item) => {
    const isUserGrantCustomerAdmin = userGrants
      .find((userGrant) => userGrant.userId === item.userId)
      ?.roleKeys?.includes(ZitadelRoles.CUSTOMER_ADMIN);

    return {
      id: item.userId,
      isAdmin: !!item.roles?.includes(ZitadelRoles.ADMIN) && !!isUserGrantCustomerAdmin,
      userId: item.userId,
    };
  });
}

type OrgNameId = {
  label: string;
  value: string;
};

type OrgType = {
  details: {
    changeDate: string;
    resourceOwner: string;
    sequence: string;
  };
  id: string;
  name: string;
  primaryDomain: string;
  state: string;
};

type ProjectType = {
  details: {
    changeDate: string;
    creationDate: string;
    resourceOwner: string;
    sequence: string;
  };
  grantId: string;
  grantOrgId: string;
  grantOrgName: string;
  grantedRoleKeys: string[];
  projectId: string;
  projectName: string;
  projectOwnerId: string;
  projectOwnerName: string;
  state: string;
};

export async function getOrganizations(): Promise<OrgNameId[]> {
  const orgs = await adminServiceV2[paths.searchOrgsMethod](paths.searchOrgs);
  const list: OrgType[] = orgs?.result ?? [];
  return list.map((org) => ({ label: org.name, value: org.id }));
}

async function listActiveUsers() {
  // The identity service's GET /users hard-codes the TYPE_HUMAN filter server-side; Zitadel's
  // POST /v2/users needs the queries body inline.
  return paths.listUsersMethod === "get"
    ? adminServiceV2.get(paths.listUsers)
    : adminServiceV2.post(paths.listUsers, { queries: [{ typeQuery: { type: "TYPE_HUMAN" } }] });
}

export const useGetActiveUsers = (isAdmin: boolean) =>
  useQuery({
    queryKey: adminQueryKeys.all,
    gcTime: QUERY_GC_TIME_MS_30M,
    queryFn: () =>
      listActiveUsers().then(async (response) => {
        const orgMembers = await getOrgMembers();
        const orgs = await getOrganizations();

        const usersList: ActiveUser[] = response?.result ?? [];
        const humanUsers = usersList.filter(
          (user): user is ActiveUser & { userId: string } => !!user.human && !!user.userId,
        );

        const activeUsersData: ActiveUserData[] = humanUsers.map((item) => ({
          updatedAt: item.details.changeDate,
          email: item.human?.email.email,
          firstName: item.human?.profile.givenName,
          fullName: `${item.human?.profile.givenName} ${item.human?.profile.familyName}`,
          id: item.userId,
          isAdmin: false,
          lastName: item.human?.profile.familyName,
          status: item.state,
          userId: item.userId,
          orgId: item.details.resourceOwner,
        }));
        const usersListDataPresorted = activeUsersData.map((t1: ActiveUserData) => ({
          ...t1,
          ...orgMembers.find((t2: OrgMembershipList) => t2.userId === t1.userId),
        }));

        const usersGroupByOrg = usersListDataPresorted.reduce((acc: Record<string, ActiveUserData[]>, curr) => {
          const key = curr && curr.orgId;
          const newObj = acc;
          newObj[key] = newObj[key] || [];
          newObj[key].push(curr);
          return newObj;
        }, {});

        return {
          totalCount: humanUsers.length,
          data: usersGroupByOrg,
          orgs,
        };
      }),
    refetchOnWindowFocus: false,
    enabled: isAdmin,
  });

export const useCreateUser = () =>
  useMutation({
    mutationFn: async (data: NewUserData) => {
      const { formData, organizationId } = data;
      const { isAdmin, ...userNameData } = formData;
      const response: NewUserImportResponse = await adminServiceV1.post(paths.createUser, userNameData);
      const { userId } = response;

      if (isAdmin) {
        const roleObject = {
          userId,
          roles: [ZitadelRoles.ADMIN],
        };
        await adminServiceV1.post(paths.addOrgMember, roleObject);

        const projects = await searchGrantedProjects();
        const currentUserGrantedProject = projects.find((item: ProjectType) => item.projectOwnerId === organizationId);
        const { grantId, projectId } = currentUserGrantedProject;
        return adminServiceV1.post(paths.createGrant(userId), {
          projectId,
          projectGrantId: grantId,
          roleKeys: [ZitadelRoles.CUSTOMER_ADMIN],
        });
      }
      return null;
    },
  });

export const useEditUser = () =>
  useMutation({
    mutationFn: (data: { formData: UserFormData; userId: string }) =>
      adminServiceV1.put(paths.updateUser(data.userId), data.formData),
  });

export const useIsUserGrantCustomerAdmin = ({ userId }: { userId?: string }) =>
  useQuery<{
    result: Array<{
      userId: string;
      roleKeys: string[];
    }>;
  }>({
    queryKey: ["members", "_search", userId],
    queryFn: () => {
      // The identity service's GET /users/:id/grants filters server-side via path param;
      // Zitadel's POST /users/grants/_search needs the userIdQuery inline.
      if (!userId) throw new Error("userId required");
      return paths.searchGrantsForUserMethod === "get"
        ? adminServiceV1.get(paths.searchGrantsForUser(userId))
        : adminServiceV1.post(paths.searchGrantsForUser(userId), {
            queries: [{ userIdQuery: { userId } }],
          });
    },
    enabled: !!userId,
  });

export const useDeactivateUser = () =>
  useMutation({
    mutationFn: (data: DeactivateUserPayload) => adminServiceV1.post(paths.deactivateUser(data.userId)),
  });

export function reloadUsers() {
  // zitadel api needs some delay
  setTimeout(() => {
    queryClient.invalidateQueries({ queryKey: adminQueryKeys.all });
  }, ZITADEL_RELOAD_DELAY);
}
